import { Button, Modal } from '@dev-spendesk/grapes';
import { type FormikProps, useFormik } from 'formik';
import React, { useState } from 'react';

import { useFeature } from 'src/core/common/hooks/useFeature';
import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { type SyncMode } from 'src/core/modules/accounting-integration/apis/useSetSyncSettings';
import { AnalyticalFieldValueMappingSelector } from 'src/core/modules/accounting-integration/components/AnalyticalFieldMapping/AnalyticalFieldValueMappingSelector';
import {
  type AnalyticalFieldMappingV2,
  type AnalyticalFieldValueMappingV2,
} from 'src/core/modules/bookkeep';

import { CostCenterFormApprovalFlowContent } from './CostCenterFormApprovalFlowContent';
import { CostCenterFormDetailsContent } from './CostCenterFormDetailsContent';
import type { FormValues } from './formValues';
import { validateCostCenterForm } from './validator';
import type { CostCenter } from '../../costCenter';
import { type ApprovalScheme } from '../../hooks/useApprovalSchemeByCostCenterIdQuery';
import type { Member } from '../../member';

type Props = {
  costCenter?: CostCenter;
  approvalScheme?: ApprovalScheme;
  members: Member[];
  costCenters: CostCenter[];
  showBackButton?: boolean;
  onSubmit(rc: Omit<CostCenter, 'id' | 'approvalSchemeId'>): Promise<void>;
  onCancel(): void;
  valueMapping?: AnalyticalFieldValueMappingV2;
  valueInDatabase?: AnalyticalFieldValueMappingV2;
  fieldMapping: AnalyticalFieldMappingV2 | undefined;
  setValueMapping: React.Dispatch<
    React.SetStateAction<AnalyticalFieldValueMappingV2 | undefined>
  >;
  syncMode?: SyncMode;
};

type Step = 'details' | 'approvalFlow';

export const CostCenterFormModal = ({
  costCenter,
  approvalScheme,
  members,
  costCenters,
  onSubmit,
  onCancel,
  valueMapping,
  setValueMapping,
  fieldMapping,
  syncMode,
  valueInDatabase,
  showBackButton = false,
}: Props) => {
  const { t } = useTranslation('global');
  const [step, setStep] = useState<Step>('details');
  const type = costCenter ? 'edition' : 'creation';
  const isMultidimensionalApprovalWorkflowEnabled = useFeature(
    FEATURES.MULTIDIMENSIONAL_APPROVAL_WORKFLOW,
  );

  const formikProps = useFormik<FormValues>({
    enableReinitialize: false,
    validateOnChange: false,
    validateOnBlur: true,
    initialValues: {
      name: costCenter?.name ?? '',
      ownerId: costCenter?.ownerId,
      viewerIds: costCenter?.viewerIds ?? [],
      approvalRules: approvalScheme?.rules ?? [],
    },
    validate: (values) =>
      validateCostCenterForm(
        values,
        t,
        costCenter?.id,
        costCenters,
        values.ownerId ?? '',
      ),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      await onSubmit({
        name: values.name,
        ownerId: values.ownerId ?? '',
        viewerIds: values.viewerIds,
        approvalRules: values.approvalRules,
        isMultiEntity: false,
      });
      setSubmitting(false);
    },
  });

  return (
    <Modal
      isOpen
      iconName="circle-information"
      iconVariant="info"
      title={getModalTitle(type, step, t)}
      actions={getModalActions({
        type,
        step,
        formikProps,
        setStep,
        onCancel,
        translator: t,
        showBackButton,
        isMultidimensionalApprovalWorkflowEnabled,
      })}
    >
      {step === 'details' && (
        <>
          <CostCenterFormDetailsContent {...formikProps} members={members} />
          {fieldMapping &&
            fieldMapping.internalId &&
            syncMode === 'pullAndMap' && (
              <AnalyticalFieldValueMappingSelector
                selectedValue={
                  valueMapping && {
                    ...valueMapping,
                    label: valueMapping.externalName,
                    key: valueMapping.id,
                  }
                }
                values={fieldMapping.values}
                onValueSelected={(value) => {
                  setValueMapping(value);
                }}
                valueInDatabase={
                  valueInDatabase && {
                    ...valueInDatabase,
                    label: valueInDatabase.externalName,
                    key: valueInDatabase.id,
                  }
                }
              />
            )}
        </>
      )}
      {step === 'approvalFlow' && (
        <>
          {t('costCenters.modalSubtitle')}
          <CostCenterFormApprovalFlowContent
            {...formikProps}
            members={members}
          />
        </>
      )}
    </Modal>
  );
};

const getModalTitle = (
  type: 'creation' | 'edition',
  step: Step,
  translator: TGlobalFunctionTyped,
) => {
  if (type === 'creation') {
    if (step === 'details') {
      return translator('costCenters.creation.titleDetails');
    }
    return translator('costCenters.creation.titleApprovalFlow');
  }
  if (step === 'details') {
    return translator('costCenters.edition.titleDetails');
  }
  return translator('costCenters.edition.titleApprovalFlow');
};

const getModalActions = ({
  type,
  step,
  formikProps,
  setStep,
  onCancel,
  translator,
  showBackButton,
  isMultidimensionalApprovalWorkflowEnabled,
}: {
  type: 'creation' | 'edition';
  step: Step;
  formikProps: FormikProps<FormValues>;
  setStep: (step: Step) => void;
  onCancel: () => void;
  translator: TGlobalFunctionTyped;
  showBackButton?: boolean;
  isMultidimensionalApprovalWorkflowEnabled: boolean;
}) => {
  // if multidimensional approval workflow is enabled, we submit the cost center creation/edition directly
  const submitButton = isMultidimensionalApprovalWorkflowEnabled ? (
    <Button
      key="configureApproval"
      variant="primaryBrand"
      text={
        type === 'creation'
          ? translator('costCenters.creation.submit')
          : translator('costCenters.edition.submit')
      }
      isDisabled={
        Boolean(formikProps.errors.name) ||
        Boolean(formikProps.errors.ownerId) ||
        !formikProps.values.name ||
        !formikProps.values.ownerId
      }
      onClick={formikProps.submitForm}
    />
  ) : (
    <Button
      key="configureApproval"
      variant="primaryBrand"
      text={translator('costCenters.configureApprovalFlow')}
      iconPosition="right"
      iconName="chevron-right"
      isDisabled={
        Boolean(formikProps.errors.name) ||
        Boolean(formikProps.errors.ownerId) ||
        !formikProps.values.name ||
        !formikProps.values.ownerId
      }
      onClick={() => setStep('approvalFlow')}
    />
  );

  if (step === 'details') {
    return [
      <Button
        key="cancel"
        variant="secondaryNeutral"
        iconName={showBackButton ? 'chevron-left' : undefined}
        text={
          showBackButton ? translator('misc.back') : translator('misc.cancel')
        }
        onClick={onCancel}
      />,
      submitButton,
    ];
  }

  return [
    <Button
      key="back"
      variant="secondaryNeutral"
      iconName="chevron-left"
      text={translator('costCenters.back')}
      onClick={() => setStep('details')}
    />,
    <Button
      key="submit"
      variant="primaryBrand"
      text={
        type === 'creation'
          ? translator('costCenters.creation.submit')
          : translator('costCenters.edition.submit')
      }
      onClick={formikProps.submitForm}
    />,
  ];
};
